/**
 * Profile Image Upload Component
 *
 * Displays an image preview with overlay controls for uploading a new image.
 * Supports both logo (square) and banner (16:9) aspect ratios.
 */
import { Camera, Loader2 } from "lucide-react";
import { useCallback, useRef, useState, type ReactElement } from "react";
import { useProfileImageUpload } from "../hooks/useProfileImageUpload";

interface ProfileImageUploadProps {
  /** Current image URL (or null if not set) */
  readonly currentUrl: string | null | undefined;
  /** Type of image being uploaded */
  readonly imageType: "logo" | "logo-square" | "banner";
  /** Called when upload completes successfully with the r2:// URL to store */
  readonly onUploadComplete: (r2Key: string) => void;
  /** Optional className for the container */
  readonly className?: string;
}

/**
 * Get aspect ratio and default styling based on image type
 */
function getImageConfig(imageType: "logo" | "logo-square" | "banner"): {
  aspectRatio: string;
  containerClass: string;
  placeholder: string;
  imageClass: string;
} {
  // All types use same height (h-40) to align in a row; width fills container
  const baseHeight = "h-56 w-full rounded-lg";

  if (imageType === "logo") {
    return {
      aspectRatio: "16 / 9",
      containerClass: baseHeight,
      placeholder: "No logo",
      imageClass: "absolute inset-0 w-full h-full px-4 object-contain",
    };
  }
  if (imageType === "logo-square") {
    return {
      aspectRatio: "1 / 1",
      containerClass: baseHeight,
      placeholder: "No square logo",
      imageClass: "absolute inset-0 w-full h-full object-cover",
    };
  }
  return {
    aspectRatio: "16 / 9",
    containerClass: baseHeight,
    placeholder: "No banner image",
    imageClass: "absolute inset-0 w-full h-full object-cover",
  };
}

export function ProfileImageUpload({
  currentUrl,
  imageType,
  onUploadComplete,
  className = "",
}: ProfileImageUploadProps): ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, uploading, error } = useProfileImageUpload();
  const [localError, setLocalError] = useState<string | null>(null);

  const config = getImageConfig(imageType);
  const displayError = localError || error;

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setLocalError(null);

      void (async () => {
        try {
          const result = await uploadImage({ imageType, file });
          onUploadComplete(result.r2Key);
        } catch (err) {
          // Error is already set in the hook, but we can add local context
          console.error("Profile image upload error:", err);
          setLocalError(err instanceof Error ? err.message : "Upload failed");
        }

        // Reset file input so the same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      })();
    },
    [imageType, onUploadComplete, uploadImage],
  );

  return (
    <div className={`relative group ${className}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
        aria-label={`Upload ${imageType} image`}
      />

      {/* Image container */}
      <button
        type="button"
        onClick={handleClick}
        disabled={uploading}
        className={`relative overflow-hidden border-2 border-dashed border-neutral-600 hover:border-lumi-accent transition-colors cursor-pointer ${config.containerClass}`}
        style={{ aspectRatio: config.aspectRatio }}
        aria-label={`Click to upload ${imageType}`}
      >
        {/* Current image or placeholder */}
        {currentUrl ? (
          <img src={currentUrl} alt={`Provider ${imageType}`} className={config.imageClass} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-neutral-500">
            <span className="text-sm">{config.placeholder}</span>
          </div>
        )}

        {/* Hover overlay */}
        <div
          className={`absolute inset-0 bg-black/60 flex flex-col items-center justify-center transition-opacity ${
            uploading ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-white animate-spin" />
              <span className="mt-2 text-white text-sm">Uploading...</span>
            </>
          ) : (
            <>
              <Camera className="w-8 h-8 text-white" />
              <span className="mt-2 text-white text-sm">
                {currentUrl ? "Change" : "Upload"} {imageType === "logo-square" ? "square logo" : imageType}
              </span>
            </>
          )}
        </div>
      </button>

      {/* Error message */}
      {displayError && <p className="mt-2 text-sm text-red-400">{displayError}</p>}

      {/* Helper text */}
      {/* <p className="mt-1 text-xs text-neutral-500">
        {imageType === "logo"
          ? "Square image recommended (min 200×200)"
          : "16:9 aspect ratio recommended (min 1920×1080)"}
        {" • "}
        JPEG, PNG, or WebP • Max 10MB
      </p> */}
    </div>
  );
}
